import { useState, useEffect, useRef } from "react";
import Loader from "../../../components/loader/loader";
import CreateFieldModal from "../../../components/field/CreateFieldModal";
import FieldsModal from "../../../components/field/FieldsModal";
import { api } from "../../../services/api";
import {
  MdAddCircle,
  MdArrowBack,
  MdDelete,
  MdDragHandle,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
  MdSave,
} from "react-icons/md";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import SortableFieldItem from "../../../components/field/SortableFieldItem";
import SortableSectionItem from "../../../components/field/SortableSectionItem";
import "./CreateCustomization.css"; // Ensure this file now contains the correct styles
import "../Home/CustomizationHome.css"; // Import shared styles if possible, otherwise rely on CreateCustomization.css

const formatDisplay = (name) =>
  name.replace(/^hoa_/, "HOA_").replace(/^coa_/, "COA_").replace(/\brofr_/gi, "ROFR_").replace(/\brofr\b/gi, "ROFR").replace(/_/g, " ").split(" ")
    .map((word) => ["HOA", "COA", "ROFR"].includes(word.toUpperCase()) ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(" ");

const sortFieldsByDisplayName = (fieldList) =>
  [...fieldList].sort((a, b) =>
    formatDisplay(a.field_name).localeCompare(formatDisplay(b.field_name), undefined, {
      sensitivity: "base",
    })
  );

const CreateCustomization = ({ onBack, onSuccess }) => {
  const errorRef = useRef(null);
  const [error, setError] = useState("");
  const [fields, setFields] = useState([]);
  const initialSectionId = crypto.randomUUID().toString();

  const [sections, setSections] = useState([
    { id: initialSectionId, name: "New Section", collapsed: true, fields: [] },
  ]);

  const [activeSectionId, setActiveSectionId] = useState(initialSectionId);
  const [formName, setFormName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loaderText, setLoaderText] = useState("Loading...");
  const [loading, setLoading] = useState(true);
  const [showCreateFieldModal, setShowCreateFieldModal] = useState(false);
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldType, setNewFieldType] = useState("str");
  const [showFieldsModal, setShowFieldsModal] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const handleBackClick = (e) => {
    e.preventDefault();
    if (window.confirm("Go back? Unsaved changes will be lost.")) {
      onBack();
    }
  };

  useEffect(() => {
    const loadFields = async () => {
      setLoading(true);
      try {
        const [defaultFields, userFields] = await Promise.all([
          api.get("/fields"),
          fetchUserFields(),
        ]);
        const userFieldIds = userFields.map((f) => f.id);
        const merged = [...userFields, ...defaultFields.filter((f) => !userFieldIds.includes(f.id))];
        setFields(sortFieldsByDisplayName(merged));

        setSections((prevSections) => {
          if (prevSections.length === 0) {
            const newId = crypto.randomUUID().toString();
            setActiveSectionId(newId);
            return [{ id: newId, name: "New Section", collapsed: true, fields: [] }];
          }
          return prevSections;
        });
      } catch (err) {
        console.error(err);
        setError("Failed to load fields.");
      } finally {
        setLoading(false);
      }
    };
    loadFields();
  }, []);

  const fetchUserFields = async () => {
    try {
      const userFields = await api.get("/fields/user-created");
      return userFields.map((f) => ({ ...f, isUserCreated: true }));
    } catch (err) { return []; }
  };

  const activateSection = (sectionId) => {
    setActiveSectionId(sectionId);
    setSections((prevSections) =>
      prevSections.map((section) => ({
        ...section,
        collapsed: sectionId ? section.id !== sectionId : true,
      }))
    );
  };

  const addNewSection = () => {
    const newSectionId = crypto.randomUUID().toString();
    setActiveSectionId(newSectionId);
    setSections((prevSections) => [
      ...prevSections.map((section) => ({ ...section, collapsed: true })),
      { id: newSectionId, name: "New Section", collapsed: false, fields: [] },
    ]);
  };

  const handleCreateField = async () => {
    try {
      if (!newFieldName.trim()) {
        setError("Field name is required.");
        return;
      }
      await api.post("/fields", [{ field_name: newFieldName, field_type: newFieldType === "list" ? "list[str]" : "str" }]);
      setNewFieldName(""); setNewFieldType("str"); setShowCreateFieldModal(false);
      const updatedUserFields = await fetchUserFields();
      const updatedIds = new Set(updatedUserFields.map((f) => f.id));
      setFields((prev) => sortFieldsByDisplayName([...updatedUserFields, ...prev.filter((f) => !updatedIds.has(f.id))]));
      const justAdded = updatedUserFields.find((f) => f.field_name === newFieldName);
      if (justAdded && activeSectionId) {
        toggleField({ ...justAdded, displayName: formatDisplay(justAdded.field_name) });
      }
    } catch (err) { setError(`Error: ${err.message}`); }
  };

  const toggleField = (field) => {
    if (!activeSectionId) return;
    setSections((prevSections) =>
      prevSections.map((section) => {
        if (section.id !== activeSectionId) return section;
        const exists = section.fields.some((f) => f.id === field.id);
        const updatedFields = exists ? section.fields.filter((f) => f.id !== field.id)
            : [...section.fields, { ...field, displayName: formatDisplay(field.field_name) }];
        return { ...section, fields: updatedFields };
      })
    );
  };

  const handleSelectAll = () => {
    if (!activeSectionId) return;
    const filtered = sortFieldsByDisplayName(fields.filter((f) =>
      f.field_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatDisplay(f.field_name).toLowerCase().includes(searchTerm.toLowerCase())
    ));
    setSections((prevSections) =>
      prevSections.map((section) => {
        if (section.id !== activeSectionId) return section;
        const existingIds = new Set(section.fields.map((f) => f.id));
        const additions = filtered.filter((f) => !existingIds.has(f.id))
          .map((f) => ({ ...f, displayName: formatDisplay(f.field_name) }));
        return { ...section, fields: [...section.fields, ...additions] };
      })
    );
  };

  const handleDeselectAll = () => {
    if (!activeSectionId) return;
    const filteredIds = new Set(fields.filter((f) =>
      f.field_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      formatDisplay(f.field_name).toLowerCase().includes(searchTerm.toLowerCase())
    ).map((f) => f.id));
    setSections((prevSections) =>
      prevSections.map((section) => {
        if (section.id !== activeSectionId) return section;
        return { ...section, fields: section.fields.filter((f) => !filteredIds.has(f.id)) };
      })
    );
  };

  const updateDisplayName = (sectionId, index, value) => {
    setSections((prev) => prev.map((s) => s.id !== sectionId ? s : { ...s, fields: s.fields.map((f, i) => i === index ? { ...f, displayName: value } : f) }));
  };

  const handleDragEnd = (event, sectionId) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSections((prev) => prev.map((section) => {
        if (section.id !== sectionId) return section;
        const oldIndex = section.fields.findIndex((f) => f.id === active.id);
        const newIndex = section.fields.findIndex((f) => f.id === over.id);
        const reordered = [...section.fields];
        const [moved] = reordered.splice(oldIndex, 1);
        reordered.splice(newIndex, 0, moved);
        return { ...section, fields: reordered };
    }));
  };

  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSections((prev) => {
      const oldIndex = prev.findIndex((s) => s.id === active.id);
      const newIndex = prev.findIndex((s) => s.id === over.id);
      const reordered = [...prev];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);
      return reordered;
    });
  };

  const handleDeleteSection = (sectionId) => {
    const newSections = sections.filter((s) => s.id !== sectionId);
    const nextActiveSectionId = activeSectionId === sectionId ? newSections[0]?.id || null : activeSectionId;
    setActiveSectionId(nextActiveSectionId);
    setSections(newSections.map((section) => ({
      ...section,
      collapsed: nextActiveSectionId ? section.id !== nextActiveSectionId : true,
    })));
  };

  const openCreateFieldModal = () => {
    setError("");
    setNewFieldName("");
    setNewFieldType("str");
    setShowCreateFieldModal(true);
  };

  const handleCreate = async () => {
    try {
      if (!formName.trim()) return setError("Customization name is required.");
      setLoaderText("Creating customization...");
      setLoading(true);
      const sectionPayload = sections.map((section) => ({ name: section.name, fields: section.fields.map((f) => f.id) }));
      const createdSectionIds = await api.post("/section", sectionPayload);
      const customization = await api.post("/customization", { name: formName, sections: createdSectionIds });

      const sectionIdMap = new Map();
      sections.forEach((clientSection, index) => { if (createdSectionIds[index]) sectionIdMap.set(clientSection.id, createdSectionIds[index]); });

      const translationPayload = [];
      sections.forEach(clientSection => {
        const serverSectionId = sectionIdMap.get(clientSection.id);
        if (serverSectionId) {
          clientSection.fields.forEach(field => {
            translationPayload.push({ field_id: field.id, translation: field.displayName, section_id: serverSectionId });
          });
        }
      });
      await api.post(`/translation/${customization.id}`, translationPayload);
      onSuccess();
    } catch (err) { setError(`Error: ${err.message}`); } finally { setLoading(false); }
  };

  if (loading) return <Loader text={loaderText} />;
  const activeSection = sections.find((s) => s.id === activeSectionId);
  const activeSectionIndex = sections.findIndex((s) => s.id === activeSectionId);
  const totalFieldCount = sections.reduce((count, section) => count + section.fields.length, 0);
  const filteredFields = sortFieldsByDisplayName(fields.filter((f) =>
    f.field_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    formatDisplay(f.field_name).toLowerCase().includes(searchTerm.toLowerCase())
  ));

  return (
    <div className="customization-content-wrapper">
      <div className="content-body animate-fade-in">
        
        {/* Header Section */}
        <div className="builder-header">
          <button className="secondary-button compact-btn back-button" onClick={handleBackClick}>
            <MdArrowBack /> Back
          </button>
          <div>
            <h1>Create Customization</h1>
            <div className="compact-meta-row">
              <span>{sections.length} sections</span>
              <span>{totalFieldCount} fields</span>
              <span>Target: {activeSection ? `${activeSectionIndex + 1}. ${activeSection.name}` : "None"}</span>
            </div>
          </div>
        </div>

        <div ref={errorRef}>{error && <div className="error-banner">{error}</div>}</div>

        {/* Top Controls: Buttons & Name Input */}
        <div className="builder-command-bar">
          <div className="builder-name-control">
            <div className="input-group inline-name-group">
                <label className="input-label">Customization Name</label>
                <input
                type="text"
                className="text-input"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="E.g., Residential Purchase Agreement"
                style={{ borderColor: !formName.trim() && error ? "var(--brand-red)" : undefined }}
                />
            </div>
          </div>

           <div className="builder-action-group">
                <button type="button" className="secondary-button compact-btn" onClick={addNewSection}>
                  <MdAddCircle /> New Section
                </button>
                <button className="primary-button compact-btn builder-save-button" onClick={handleCreate}>
                  <MdSave /> Save
                </button>
           </div>
        </div>

        {/* Main Content: Sidebar + Drag Area */}
        <div className="customization-layout">
            
            {/* Sidebar: Available Fields */}
            <div className={`field-selector-container ${isSidebarCollapsed ? 'collapsed' : ''}`}>
            <select className="select-input section-picker" value={activeSectionId || ""} onChange={(e) => activateSection(e.target.value !== "" ? e.target.value : null)}>
                <option value="">Select target section</option>
                {sections.map((section) => (<option key={section.id} value={section.id}>{section.name}</option>))}
            </select>
            <div className="field-selector-header" onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}>
                <div>
                  <h3>Available Fields</h3>
                </div>
                <span className="collapse-icon">
                    {isSidebarCollapsed ? <MdKeyboardArrowDown /> : <MdKeyboardArrowUp />}
                </span>
            </div>
            
            {!isSidebarCollapsed && (
                <div className="field-selector-content">
                    <input type="text" className="text-input" placeholder="Search fields..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{marginBottom:'1rem'}} />
                    
                    <div className="field-pane-actions">
                        <button type="button" className="secondary-button compact-btn" onClick={openCreateFieldModal}>
                          <MdAddCircle /> Add Field
                        </button>
                        <button className="secondary-button compact-btn" onClick={handleSelectAll} disabled={!activeSectionId}>Select All</button>
                        <button className="secondary-button compact-btn" onClick={handleDeselectAll} disabled={!activeSectionId}>Deselect All</button>
                    </div>

                    <div className="fields-list-scroll">
                        {filteredFields.map((field) => {
                            const isChecked = !!activeSection?.fields.find((f) => f.id === field.id);
                            const isDisabled = !activeSectionId;
                            return (
                                <label key={field.id} className={`field-checkbox-label ${isChecked ? "is-selected" : ""} ${isDisabled ? "is-disabled" : ""}`}>
                                <input type="checkbox" checked={isChecked} onChange={() => toggleField(field)} disabled={isDisabled} />
                                <span className="field-name-text">{formatDisplay(field.field_name)}</span>
                                </label>
                            );
                        })}
                        {filteredFields.length === 0 && <div className="empty-section-placeholder">No fields found.</div>}
                    </div>
                </div>
            )}
            </div>

            {/* Main: Sections List */}
            <div className="sections-container">
            <DndContext collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
                <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                {sections.map((section) => (
                    <SortableSectionItem key={section.id} id={section.id}>
                    {({ handleListeners }) => (
                        <div className={`field-section ${section.id === activeSectionId ? "active-section" : ""}`}>
                        <div className="field-section-header">
                            <MdDragHandle {...handleListeners} style={{ cursor: "grab", fontSize: "20px", color: "var(--text-secondary)" }} />
                            <input type="text" value={section.name} onChange={(e) => { const name = e.target.value; setSections((prev) => prev.map((s) => s.id === section.id ? { ...s, name } : s)); }} className="text-input no-drag section-title-input" />
                            <span className="section-field-count">{section.fields.length} fields</span>
                            <div className="section-actions-icons">
                            <button className={`target-section-btn no-drag ${section.id === activeSectionId ? "active" : ""}`} onClick={(e) => { e.stopPropagation(); activateSection(section.id); }}>{section.id === activeSectionId ? "Editing" : "Edit"}</button>
                            <button className="section-toggle-btn no-drag" onClick={(e) => { e.stopPropagation(); setSections((prev) => prev.map((s) => s.id === section.id ? { ...s, collapsed: !s.collapsed } : s)); }}>{section.collapsed ? "View" : "Collapse"}</button>
                            <button type="button" className="delete-section-btn no-drag" aria-label={`Delete ${section.name}`} title="Delete section" onClick={(e) => { e.stopPropagation(); setSectionToDelete(section.id); setShowDeleteConfirm(true); }}><MdDelete /></button>
                            </div>
                        </div>
                        {!section.collapsed && (
                            <div className="field-section-content">
                            <DndContext collisionDetection={closestCenter} onDragEnd={(e) => handleDragEnd(e, section.id)}>
                                <SortableContext items={section.fields.map((f) => f.id)} strategy={verticalListSortingStrategy}>
                                {section.fields.map((field, index) => (
                                    <SortableFieldItem key={field.id} id={field.id} field={field} index={index} updateDisplayName={(val) => updateDisplayName(section.id, index, val)} />
                                ))}
                                {section.fields.length === 0 && <div className="empty-section-placeholder">No fields in this section. Select fields from the sidebar.</div>}
                                </SortableContext>
                            </DndContext>
                            </div>
                        )}
                        </div>
                    )}
                    </SortableSectionItem>
                ))}
                </SortableContext>
            </DndContext>
            </div>
        </div>

      </div>

        {/* Modals */}
      <CreateFieldModal show={showCreateFieldModal} onClose={() => setShowCreateFieldModal(false)} onCreate={handleCreateField} newFieldName={newFieldName} setNewFieldName={setNewFieldName} newFieldType={newFieldType} setNewFieldType={setNewFieldType} />
      {showFieldsModal && <FieldsModal token={localStorage.getItem("access_token")} onClose={() => setShowFieldsModal(false)} />}
      
      {showDeleteConfirm && (
        <div className="provider-overlay">
          <div className="provider-window delete-window">
             <div className="provider-body-scroll center-content">
                <p>Delete this section?</p>
                <div className="delete-actions">
                    <button className="primary-button danger-bg" onClick={() => { handleDeleteSection(sectionToDelete); setShowDeleteConfirm(false); setSectionToDelete(null); }}>Yes, Delete</button>
                    <button className="secondary-button" onClick={() => { setShowDeleteConfirm(false); setSectionToDelete(null); }}>Cancel</button>
                </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default CreateCustomization;
